import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../translations';

const LanguageContext = createContext(null);

const SUPPORTED_LANGS = ['fr', 'en'];
const DEFAULT_LANG = 'fr';

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANG;
    const saved = localStorage.getItem('lang');
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    // Détection auto depuis la langue du navigateur
    const browserLang = navigator.language?.slice(0, 2);
    return SUPPORTED_LANGS.includes(browserLang) ? browserLang : DEFAULT_LANG;
  });

  const setLang = useCallback((newLang) => {
    if (SUPPORTED_LANGS.includes(newLang)) {
      setLangState(newLang);
      localStorage.setItem('lang', newLang);
      document.documentElement.lang = newLang;
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // t('key') ou t('section.key') — fallback sur la clé si introuvable
  const t = useCallback((key) => {
    const keys = key.split('.');
    let result = translations[lang];
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        // Fallback en français si traduction EN manquante
        if (lang !== DEFAULT_LANG) {
          let fallback = translations[DEFAULT_LANG];
          for (const kk of keys) {
            if (fallback && typeof fallback === 'object' && kk in fallback) {
              fallback = fallback[kk];
            } else {
              return key; // dernière chance : retourne la clé brute
            }
          }
          return (typeof fallback === 'string' || typeof fallback === 'function') ? fallback : key;
        }
        return key;
      }
    }
    return (typeof result === 'string' || typeof result === 'function') ? result : key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang doit être appelé dans un LanguageProvider');
  return ctx;
}
